import type { FieldBlock } from "block-system/blocks/Form/Field/types";
import { isNumeric } from "utils/isNumeric";
import { isValid as isValidDate } from "date-fns";
import { UploadcareFile } from "@uploadcare/react-uploader";

export function fieldBlockToTableValue(
  field: FieldBlock,
  submissionValue: any
) {
  if (!field.config.tablesFieldKey) {
    throw new Error("Only to be used with table connected fields");
  }

  switch (field.config.inputType) {
    case "dropdown":
      if (!submissionValue) {
        return { [field.config.tablesFieldKey]: null };
      }

      let value;
      if (field.config.tableId) {
        // dynamic dropdown populated from Tables
        value = submissionValue;
      } else if (field.config.multiSelect && Array.isArray(submissionValue)) {
        value = submissionValue?.map((v: string) => ({ label: v, value: v }));
      } else {
        value = { label: submissionValue, value: submissionValue };
      }
      return {
        [field.config.tablesFieldKey]: value,
      };

    case "file-upload":
      if (!submissionValue) {
        return { [field.config.tablesFieldKey]: null };
      }
      // Because we now share the same field key for single and multiple file upload, we need to check if the submission
      // is an array or a single file upload. When we remove the multiple file upload feature flag, this check will be
      // removed. This will always return an array of length 1, so we just always take the first element.
      const singleFileUploadSubmission = submissionValue as
        | UploadcareFile[]
        | UploadcareFile;
      const file = Array.isArray(singleFileUploadSubmission)
        ? singleFileUploadSubmission[0]
        : singleFileUploadSubmission;
      return {
        [field.config.tablesFieldKey]: {
          link: file.cdnUrl,
          text: file.name,
        },
      };

    case "multiple-file-upload":
      if (!submissionValue) return { [field.config.tablesFieldKey]: null };
      // If interface owners have already crated a multiple file upload field, and we turn off the feature flag, we will
      // fallback to a url input field. Users can enter comma separated urls instead. But the config still stays as
      // multiple-file-upload. When we cleanup the feature flag, this will be types as FileInfo[] only and expect only arrays
      const files = submissionValue as UploadcareFile[] | string;
      return {
        [field.config.tablesFieldKey]: Array.isArray(files)
          ? files.map((file) => ({
              link: file.cdnUrl,
              text: file.name,
            }))
          : files.split(",").map((url: string) => ({
              link: url.trim(),
              text: url.trim(),
            })),
      };

    case "url":
      if (!submissionValue) {
        return { [field.config.tablesFieldKey]: null };
      }
      return {
        [field.config.tablesFieldKey]: {
          link: submissionValue,
          text: submissionValue,
        },
      };

    case "multi-url":
      if (!submissionValue) {
        return { [field.config.tablesFieldKey]: null };
      }
      return {
        [field.config.tablesFieldKey]: submissionValue
          .split(",")
          .map((url: string) => ({
            link: url.trim(),
            text: url.trim(),
          })),
      };
    case "currency":
      if (!submissionValue) {
        return { [field.config.tablesFieldKey]: null };
      }
      return {
        [field.config.tablesFieldKey]: submissionValue,
      };

    case "date-picker":
      const date = new Date(submissionValue);
      if (!submissionValue || !isValidDate(date)) {
        return { [field.config.tablesFieldKey]: null };
      }

      return { [field.config.tablesFieldKey]: date.toISOString() };

    case "number":
      const formattedValue = isNumeric(submissionValue)
        ? Number(submissionValue)
        : undefined;
      return { [field.config.tablesFieldKey]: formattedValue };

    default:
      return { [field.config.tablesFieldKey]: submissionValue };
  }
}

export function formSubmissionToTableValues(
  values: Record<string, any>,
  fieldBlocks: FieldBlock[]
) {
  const keys = Object.keys(values);
  return keys.reduce((prev: any, key: string) => {
    const field = fieldBlocks.find((fieldBlock: FieldBlock) => {
      return fieldBlock.config.id === key;
    });

    if (!field) return prev;
    if (!field.config.tablesFieldKey) return prev;

    const value = fieldBlockToTableValue(field, values[key]);
    return { ...prev, ...value };
  }, {});
}

// When converting the edit/create table record form to table values, we need to use the field name instead of the id
export function tableRecordFormToTableValues(
  values: Record<string, unknown>,
  fieldBlocks: FieldBlock[]
) {
  return Object.keys(values).reduce((tableValues: any, key: string) => {
    const field = fieldBlocks.find((fieldBlock: FieldBlock) => {
      return fieldBlock.config.name === key;
    });

    if (!field) {
      return tableValues;
    }

    if (!field.config.tablesFieldKey) {
      return tableValues;
    }

    if (field.config.readOnly) {
      return tableValues;
    }

    const value = fieldBlockToTableValue(field, values[key]);

    return { ...tableValues, ...value };
  }, {});
}
